import type { BrowserLocalCacheSensitivity } from './types'

const FORBIDDEN_FIELD_PATTERNS = [
  /^authorization$/i,
  /^cookie$/i,
  /^set-cookie$/i,
  /(^|[_-])token($|[_-])/i,
  /(^|[_-])access[_-]?token($|[_-])/i,
  /(^|[_-])refresh[_-]?token($|[_-])/i,
  /(^|[_-])api[_-]?key($|[_-])/i,
  /(^|[_-])secret($|[_-])/i,
  /(^|[_-])password($|[_-])/i,
  /(^|[_-])sms[_-]?code($|[_-])/i,
  /(^|[_-])verification[_-]?code($|[_-])/i,
  /(^|[_-])payment($|[_-])/i,
  /(^|[_-])card($|[_-])/i,
]

const HIGH_SENSITIVITY_HINTS = [
  'order',
  'fulfillment',
  'shipping',
  'address',
  'phone',
  'store-binding',
  'pricing',
  'draft',
  'publish',
]

export const isForbiddenCacheFieldName = (fieldName: string): boolean =>
  FORBIDDEN_FIELD_PATTERNS.some((pattern) => pattern.test(fieldName))

export const classifyLocalCacheSensitivity = (resourceName: string): BrowserLocalCacheSensitivity => {
  const normalized = String(resourceName || '').trim().toLowerCase()
  if (!normalized) return 'medium'
  if (normalized.includes('auth') || normalized.includes('payment')) return 'forbidden'
  if (HIGH_SENSITIVITY_HINTS.some((hint) => normalized.includes(hint))) return 'high'
  if (normalized.includes('public') || normalized.includes('app-config')) return 'public'
  return 'medium'
}

export const sanitizeLocalCachePayload = <TValue>(value: TValue): TValue => {
  if (!value || typeof value !== 'object') {
    return value
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeLocalCachePayload(item)) as TValue
  }
  if (value instanceof Blob || value instanceof ArrayBuffer) {
    return value
  }
  const next: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (isForbiddenCacheFieldName(key)) {
      continue
    }
    next[key] = sanitizeLocalCachePayload(item)
  }
  return next as TValue
}

export const assertPayloadCacheable = (resourceName: string, payload: unknown): void => {
  const sensitivity = classifyLocalCacheSensitivity(resourceName)
  if (sensitivity === 'forbidden') {
    throw new Error(`Resource is forbidden for browser local cache: ${resourceName}`)
  }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return
  }
  const forbiddenKeys = Object.keys(payload as Record<string, unknown>).filter(isForbiddenCacheFieldName)
  if (forbiddenKeys.length > 0) {
    throw new Error(`Payload contains forbidden local-cache fields: ${forbiddenKeys.join(',')}`)
  }
}

